'use client';

import { useEffect, useRef } from 'react';

/**
 * 2026-06-05 — minimal in-page dialog matching the cream/gold theme.
 * Replaces window.alert() and window.confirm() in admin flows where
 * the browser's native chrome looked alien against the rest of the
 * UI ("staging.chiefeco.ai 显示" header etc).
 *
 * Single dialog component drives both notice (just an OK button) and
 * confirm (cancel + confirm). Caller picks via the optional onConfirm
 * prop: present = confirm dialog, absent = notice dialog. Renders
 * nothing when open=false so the parent can flip a boolean to control.
 *
 * No portal — relies on z-50 + position:fixed and inherits the
 * surrounding font stack so the type matches the page.
 */
export function AppDialog({
  open,
  title,
  message,
  tone = 'neutral',
  confirmLabel = '确定',
  cancelLabel = '取消',
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  message: string;
  tone?: 'neutral' | 'warn' | 'danger';
  confirmLabel?: string;
  cancelLabel?: string;
  /** When provided, dialog renders cancel + confirm. Otherwise just a single OK. */
  onConfirm?: () => void;
  onClose: () => void;
}) {
  const confirmRef = useRef<HTMLButtonElement | null>(null);

  // ESC to close + autofocus the confirm button so keyboard works.
  useEffect(() => {
    if (!open) return;
    confirmRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const accent =
    tone === 'danger'
      ? 'bg-rose-700 hover:bg-rose-700/90 text-paper'
      : tone === 'warn'
        ? 'bg-warmth-dark hover:bg-warmth text-paper'
        : 'bg-ink hover:bg-ink-mute text-paper';

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: backdrop click is a UX nicety, ESC + buttons handle a11y
    // biome-ignore lint/a11y/useSemanticElements: <dialog> doesn't give us the backdrop + custom focus management we need here
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-ink/40 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-md bg-paper border border-line rounded-r-md shadow-md p-5 sm:p-6">
        <h2 className="font-display text-xl text-ink mb-2 leading-tight">{title}</h2>
        <p className="text-sm sm:text-[15px] text-ink-mute leading-relaxed whitespace-pre-wrap mb-5">
          {message}
        </p>
        <div className="flex items-center justify-end gap-2">
          {onConfirm && (
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-r-xs border border-line text-ink-mute text-sm hover:text-ink hover:border-line-strong transition-colors"
            >
              {cancelLabel}
            </button>
          )}
          <button
            ref={confirmRef}
            type="button"
            onClick={onConfirm ?? onClose}
            className={`px-4 py-2 rounded-r-xs text-sm transition-colors ${accent}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
